/**
 * BoardPrepWizard — A guided 5-step flow to prepare boards for analytics.
 *
 * Steps:
 * 1. Select target board(s)
 * 2. Choose analytics goal (Sprint Tracking or Kanban Flow)
 * 3. Review board readiness
 * 4. Apply changes (create fields, set isDone)
 * 5. Confirmation + Create Dashboard
 *
 * Requirements: 10.1–10.10
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import * as api from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Board {
  readonly boardId: string;
  readonly title: string;
}

interface FieldTemplate {
  readonly templateId: string;
  readonly name: string;
  readonly description: string;
  readonly fields: { name: string; type: string; description: string; defaultOptions?: string[] }[];
}

interface FieldReadinessItem {
  readonly fieldName: string;
  readonly requiredType: string;
  readonly status: 'exists' | 'missing' | 'type-conflict';
  readonly existingType?: string;
}

interface BoardReadiness {
  readonly boardId: string;
  readonly boardTitle: string;
  readonly hasDoneList: boolean;
  readonly doneListCandidates: { listId: string; title: string }[];
  readonly fieldStatus: FieldReadinessItem[];
}

interface ApplyResult {
  readonly action: string;
  readonly field?: string;
  readonly listId?: string;
  readonly status: 'created' | 'skipped' | 'failed';
  readonly reason?: string;
}

type WizardStep = 1 | 2 | 3 | 4 | 5;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BoardPrepWizard(): React.ReactElement {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1: Board selection
  const [availableBoards, setAvailableBoards] = useState<Board[]>([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(true);

  // Step 2: Goal selection
  const [templates, setTemplates] = useState<FieldTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Step 3: Readiness
  const [readiness, setReadiness] = useState<BoardReadiness[]>([]);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [selectedDoneLists, setSelectedDoneLists] = useState<Record<string, string[]>>({});

  // Step 4: Apply
  const [applying, setApplying] = useState(false);
  const [applyResults, setApplyResults] = useState<ApplyResult[]>([]);

  // Step 5: Dashboard creation
  const [creating, setCreating] = useState(false);
  const [sprintOptions, setSprintOptions] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  // Data Fetching
  // ---------------------------------------------------------------------------

  useEffect(() => {
    async function loadBoards() {
      try {
        const response = await api.get<{ data: Board[] }>('/v1/boards');
        setAvailableBoards(response.data ?? []);
      } catch {
        setAvailableBoards([]);
      } finally {
        setBoardsLoading(false);
      }
    }
    loadBoards();
  }, []);

  useEffect(() => {
    async function loadTemplates() {
      try {
        const response = await api.get<{ data: FieldTemplate[] }>('/v1/analytics/templates/fields');
        setTemplates(response.data ?? []);
      } catch {
        setTemplates([]);
      }
    }
    loadTemplates();
  }, []);

  // ---------------------------------------------------------------------------
  // Step Navigation
  // ---------------------------------------------------------------------------

  const goNext = useCallback(() => setStep((s) => Math.min(5, s + 1) as WizardStep), []);
  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1) as WizardStep), []);

  // ---------------------------------------------------------------------------
  // Step 3: Check Readiness
  // ---------------------------------------------------------------------------

  const checkReadiness = useCallback(async () => {
    setReadinessLoading(true);
    try {
      const response = await api.post<{ data: BoardReadiness[] }>('/v1/analytics/wizard/check-readiness', {
        boardIds: selectedBoardIds,
        templateId: selectedTemplateId,
      });
      setReadiness(response.data ?? []);
    } catch {
      setReadiness([]);
    } finally {
      setReadinessLoading(false);
    }
  }, [selectedBoardIds, selectedTemplateId]);

  useEffect(() => {
    if (step === 3 && selectedBoardIds.length > 0 && selectedTemplateId) {
      checkReadiness();
    }
  }, [step, checkReadiness, selectedBoardIds.length, selectedTemplateId]);

  // ---------------------------------------------------------------------------
  // Step 4: Apply
  // ---------------------------------------------------------------------------

  const handleApply = useCallback(async () => {
    setApplying(true);
    const allResults: ApplyResult[] = [];

    for (const boardId of selectedBoardIds) {
      try {
        const response = await api.post<{ data: ApplyResult[] }>('/v1/analytics/wizard/apply', {
          boardId,
          templateId: selectedTemplateId,
          listsToMarkDone: selectedDoneLists[boardId] ?? [],
          sprintOptions: sprintOptions.length > 0 ? sprintOptions : undefined,
        });
        allResults.push(...(response.data ?? []));
      } catch (err) {
        allResults.push({ action: 'apply', status: 'failed', reason: err instanceof Error ? err.message : 'Unknown error' });
      }
    }

    setApplyResults(allResults);
    setApplying(false);
    goNext();
  }, [selectedBoardIds, selectedTemplateId, selectedDoneLists, sprintOptions, goNext]);

  // ---------------------------------------------------------------------------
  // Step 5: Create Dashboard
  // ---------------------------------------------------------------------------

  const handleCreateDashboard = useCallback(async () => {
    setCreating(true);
    try {
      // Find the dashboard template that matches the field template
      const dashTemplateId = selectedTemplateId === 'sprint-tracking' ? 'sprint-health' : 'kanban-flow';
      const response = await api.post<{ data: { dashboardId: string } }>('/v1/analytics/templates/dashboards/instantiate', {
        templateId: dashTemplateId,
        boardIds: selectedBoardIds,
      });
      if (response.data?.dashboardId) {
        navigate(`/analytics/dashboards/${response.data.dashboardId}`);
      }
    } catch {
      // Stay on page if creation fails
    } finally {
      setCreating(false);
    }
  }, [selectedTemplateId, selectedBoardIds, navigate]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <main className="min-h-screen bg-background p-6" role="main">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Prepare Board for Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up your boards with the right fields and configuration for analytics dashboards.
          </p>
        </header>

        {/* Step Indicator */}
        <nav aria-label="Wizard progress" className="mb-8">
          <ol className="flex items-center gap-2">
            {(['Select Boards', 'Choose Goal', 'Review', 'Apply', 'Done'] as const).map((label, i) => {
              const stepNum = (i + 1) as WizardStep;
              const isActive = step === stepNum;
              const isComplete = step > stepNum;
              return (
                <li key={label} className="flex items-center gap-2">
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                      isActive ? 'bg-primary text-primary-foreground' :
                      isComplete ? 'bg-green-600 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    {isComplete ? '✓' : stepNum}
                  </span>
                  <span className={`text-sm ${isActive ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {label}
                  </span>
                  {i < 4 && <span className="mx-2 h-px w-6 bg-border" aria-hidden="true" />}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Step Content */}
        <section aria-label={`Step ${step}`}>
          {step === 1 && (
            <Step1SelectBoards
              boards={availableBoards}
              loading={boardsLoading}
              selected={selectedBoardIds}
              onSelect={setSelectedBoardIds}
            />
          )}
          {step === 2 && (
            <Step2ChooseGoal
              templates={templates}
              selected={selectedTemplateId}
              onSelect={setSelectedTemplateId}
            />
          )}
          {step === 3 && (
            <Step3ReviewReadiness
              readiness={readiness}
              loading={readinessLoading}
              selectedDoneLists={selectedDoneLists}
              onDoneListChange={setSelectedDoneLists}
            />
          )}
          {step === 4 && (
            <Step4Apply results={applyResults} applying={applying} />
          )}
          {step === 5 && (
            <Step5Confirmation
              results={applyResults}
              creating={creating}
              onCreateDashboard={handleCreateDashboard}
            />
          )}
        </section>

        {/* Navigation Buttons */}
        <footer className="mt-8 flex items-center justify-between border-t border-border pt-4">
          <div>
            {step > 1 && step < 5 && (
              <Button variant="outline" onClick={goBack}>Back</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/analytics')}>
              {step === 5 ? 'Close' : 'Cancel'}
            </Button>
            {step === 1 && (
              <Button onClick={goNext} disabled={selectedBoardIds.length === 0}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button onClick={goNext} disabled={!selectedTemplateId}>
                Next
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handleApply} disabled={applying}>
                {applying ? 'Applying…' : 'Apply Changes'}
              </Button>
            )}
            {step === 3 && (
              <Button variant="outline" onClick={() => setStep(5)}>
                Skip
              </Button>
            )}
          </div>
        </footer>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Step Components
// ---------------------------------------------------------------------------

function Step1SelectBoards({ boards, loading, selected, onSelect }: {
  boards: Board[];
  loading: boolean;
  selected: string[];
  onSelect: (ids: string[]) => void;
}): React.ReactElement {
  if (loading) {
    return <p className="text-muted-foreground">Loading boards…</p>;
  }

  const toggle = (boardId: string) => {
    onSelect(
      selected.includes(boardId)
        ? selected.filter((id) => id !== boardId)
        : [...selected, boardId].slice(0, 10),
    );
  };

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold">Select Boards</h2>
      <p className="mb-4 text-sm text-muted-foreground">Choose up to 10 boards to prepare for analytics.</p>
      <div className="space-y-2" role="listbox" aria-multiselectable="true" aria-label="Available boards">
        {boards.map((board) => (
          <label
            key={board.boardId}
            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
              selected.includes(board.boardId) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
          >
            <input
              type="checkbox"
              checked={selected.includes(board.boardId)}
              onChange={() => toggle(board.boardId)}
              className="h-4 w-4 rounded border-gray-300"
              aria-label={board.title}
            />
            <span className="text-sm font-medium">{board.title}</span>
          </label>
        ))}
      </div>
      {boards.length === 0 && <p className="text-sm text-muted-foreground">No boards available.</p>}
    </div>
  );
}

function Step2ChooseGoal({ templates, selected, onSelect }: {
  templates: FieldTemplate[];
  selected: string;
  onSelect: (id: string) => void;
}): React.ReactElement {
  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold">Choose Analytics Goal</h2>
      <p className="mb-4 text-sm text-muted-foreground">Select what kind of analytics you want to enable.</p>
      <div className="grid gap-4 sm:grid-cols-2" role="radiogroup" aria-label="Analytics goal">
        {templates.map((t) => (
          <label
            key={t.templateId}
            className={`flex cursor-pointer flex-col rounded-lg border p-4 transition-colors ${
              selected === t.templateId ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
            }`}
          >
            <input
              type="radio"
              name="goal"
              value={t.templateId}
              checked={selected === t.templateId}
              onChange={() => onSelect(t.templateId)}
              className="sr-only"
            />
            <span className="text-sm font-semibold">{t.name}</span>
            <span className="mt-1 text-xs text-muted-foreground">{t.description}</span>
            <ul className="mt-3 space-y-1">
              {t.fields.map((f) => (
                <li key={f.name} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{f.name}</span> ({f.type}) — {f.description}
                </li>
              ))}
            </ul>
          </label>
        ))}
      </div>
    </div>
  );
}

function Step3ReviewReadiness({ readiness, loading, selectedDoneLists, onDoneListChange }: {
  readiness: BoardReadiness[];
  loading: boolean;
  selectedDoneLists: Record<string, string[]>;
  onDoneListChange: (v: Record<string, string[]>) => void;
}): React.ReactElement {
  if (loading) {
    return <p className="text-muted-foreground">Checking board readiness…</p>;
  }

  const toggleDoneList = (boardId: string, listId: string) => {
    const current = selectedDoneLists[boardId] ?? [];
    const updated = current.includes(listId)
      ? current.filter((id) => id !== listId)
      : [...current, listId];
    onDoneListChange({ ...selectedDoneLists, [boardId]: updated });
  };

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold">Review Board Readiness</h2>
      <div className="space-y-6">
        {readiness.map((board) => (
          <div key={board.boardId} className="rounded-lg border border-border p-4">
            <h3 className="text-sm font-semibold">{board.boardTitle}</h3>

            {/* Field status */}
            <ul className="mt-3 space-y-1">
              {board.fieldStatus.map((f) => (
                <li key={f.fieldName} className="flex items-center gap-2 text-sm">
                  <span className={`inline-block h-2 w-2 rounded-full ${
                    f.status === 'exists' ? 'bg-green-500' :
                    f.status === 'missing' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`} />
                  <span className="font-medium">{f.fieldName}</span>
                  <span className="text-muted-foreground">
                    {f.status === 'exists' && '— exists'}
                    {f.status === 'missing' && '— will be created'}
                    {f.status === 'type-conflict' && `— conflict (existing: ${f.existingType})`}
                  </span>
                </li>
              ))}
            </ul>

            {/* Done list status */}
            <div className="mt-3">
              {board.hasDoneList ? (
                <p className="flex items-center gap-2 text-sm">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                  Done list configured
                </p>
              ) : (
                <div>
                  <p className="flex items-center gap-2 text-sm text-yellow-700">
                    <span className="inline-block h-2 w-2 rounded-full bg-yellow-500" />
                    No done list — select which list(s) represent "done":
                  </p>
                  <div className="mt-2 ml-4 space-y-1">
                    {board.doneListCandidates.map((list) => (
                      <label key={list.listId} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(selectedDoneLists[board.boardId] ?? []).includes(list.listId)}
                          onChange={() => toggleDoneList(board.boardId, list.listId)}
                          className="h-3.5 w-3.5 rounded border-gray-300"
                        />
                        {list.title}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step4Apply({ results, applying }: {
  results: ApplyResult[];
  applying: boolean;
}): React.ReactElement {
  if (applying) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
        <p className="text-sm text-muted-foreground">Applying changes…</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold">Changes Applied</h2>
      <ul className="space-y-2">
        {results.map((r, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <span className={`inline-block h-2 w-2 rounded-full ${
              r.status === 'created' ? 'bg-green-500' :
              r.status === 'skipped' ? 'bg-gray-400' :
              'bg-red-500'
            }`} />
            <span className="font-medium">{r.field ?? r.listId ?? r.action}</span>
            <span className="text-muted-foreground">
              — {r.status}{r.reason ? ` (${r.reason})` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step5Confirmation({ results, creating, onCreateDashboard }: {
  results: ApplyResult[];
  creating: boolean;
  onCreateDashboard: () => void;
}): React.ReactElement {
  const created = results.filter((r) => r.status === 'created').length;
  const skipped = results.filter((r) => r.status === 'skipped').length;
  const failed = results.filter((r) => r.status === 'failed').length;

  return (
    <div className="text-center py-8">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold">Board Preparation Complete</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {created > 0 && `${created} field${created !== 1 ? 's' : ''} created. `}
        {skipped > 0 && `${skipped} skipped. `}
        {failed > 0 && `${failed} failed. `}
        {created === 0 && skipped === 0 && failed === 0 && 'All fields already exist. '}
      </p>
      <div className="mt-6">
        <Button onClick={onCreateDashboard} disabled={creating}>
          {creating ? 'Creating Dashboard…' : 'Create Dashboard from Template'}
        </Button>
      </div>
    </div>
  );
}
